/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import firebase from 'firebase/compat/app';

import { firstValueFrom, from, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { delayedRetry } from 'src/app/plain-javascript/services/rxjs-internal-extensions';

import { FancyActionTrackerFactory } from 'src/app/common/fancy-firebase/services/fancy-action-tracker-factory.service';
import { FancyIdJoin } from 'src/app/common/fancy-firebase/base/FancyIdJoin';
import {
	FancyListIndexDumpFactory,
	FancyListIndexFragmenter,
} from 'src/app/common/fancy-firebase/services/factories';

import { TicketListFactory } from 'src/app/common/fancy-firebase-ticket/services/ticket-factory';
import { TimelineTicketListFactory } from 'src/app/common/fancy-firebase-ticket/services/ticket-factories';
import { Dependencies } from 'src/app/app-data/models/Dependencies';
import { fancyFirebaseReferencer, fancyFirebaseGetters } from '@touchplan/fancy-referencer';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';

@Injectable({
	providedIn: 'root',
})
export class LegacyHelperService {
	private logger: ILoggerService;
	constructor(
		private ticketListFactory: TicketListFactory,
		private timelineTicketListFactory: TimelineTicketListFactory,
		private timelineTicketListPlaceholderFactory: FancyListIndexDumpFactory,
		private angularFireDatabase: AngularFireDatabase,
		private fancyActionTrackerFactory: FancyActionTrackerFactory
	) {
		this.logger = LoggerServiceFactory.create('LegacyHelperService');
	}

	public getDependencyListSync(projectId: string, planId: string) {
		const angularLand = {
			angularFireDatabase: this.angularFireDatabase,
			fancyActionTrackerFactory: this.fancyActionTrackerFactory,
		};
		return firstValueFrom(
			from(this.preloadDependencyData(projectId, planId)).pipe(
				switchMap((preloadData) => {
					const numTickets = preloadData[0].numChildren() + preloadData[1].length;

					const ticketListPath = fancyFirebaseGetters.getTicketPath(projectId, planId);
					const ticketList = this.ticketListFactory.create(
						fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketListPath, projectId)
					);
					ticketList.suppressActionsForLoading = false;

					const ticketsPath = fancyFirebaseGetters.getTicketPath(projectId);
					const timelineTicketFragment = new FancyListIndexFragmenter(
						angularLand,
						fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketsPath, projectId)
					);

					const projectRefPath = fancyFirebaseGetters.getTicketPath(projectId);
					const timelineTicketList = this.timelineTicketListFactory.create(timelineTicketFragment.emitter$);
					timelineTicketList.projectRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
						projectRefPath,
						projectId
					);
					timelineTicketFragment.start();

					const timelineTicketsListPath = fancyFirebaseGetters.getTimelineTicketsPath(projectId);
					const timelineTicketFragmentJoiner = new FancyIdJoin(
						'$id',
						timelineTicketList,
						timelineTicketFragment
					);
					const timelineTicketPlaceholderList = this.timelineTicketListPlaceholderFactory.create(
						fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
							timelineTicketsListPath,
							projectId
						),
						timelineTicketFragmentJoiner
					);

					const dependencies = new Dependencies(ticketList, timelineTicketList, planId);

					return of(dependencies).pipe(
						// The retry time used to be 3000 instead of 500, but that
						// lead to a slow user experience.
						delayedRetry((list) => list.totalTicketCount === numTickets, 500, 90),
						tap((list) => {
							this.logger.log('tap fired', list);
							ticketList.destroy();
							timelineTicketFragment.destroy();
							timelineTicketList.destroy();
							timelineTicketFragmentJoiner.destroy();
							timelineTicketPlaceholderList.destroy();
						})
					);
				})
			)
		);
	}

	private preloadDependencyData(projectId: string, planId: string) {
		const ticketListPath = fancyFirebaseGetters.getTicketPath(projectId, planId);
		const timelineTicketPath = fancyFirebaseGetters.getTimelineTicketsPath(projectId);

		const ticketList = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			ticketListPath,
			projectId
		);
		const timelineIndex = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			timelineTicketPath,
			projectId
		);

		return Promise.all([
			ticketList.once('value'),
			timelineIndex.once('value').then((list) => {
				const promises: Array<Promise<firebase.database.DataSnapshot>> = [];
				list.forEach((snap) => {
					if (!snap.child('planId').exists() || !snap.child('ticketId').exists()) {
						this.logger.log('broken timeline index: ', snap.key, snap);
						return;
					}
					const ticketRefPath = fancyFirebaseGetters.getTicketPath(
						projectId,
						snap.child('planId').val(),
						snap.child('ticketId').val()
					);
					const ticketRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
						ticketRefPath,
						projectId
					);
					promises.push(ticketRef.once('value'));
				});
				return Promise.all(promises).then((p) => {
					const smallerList: typeof p = [];
					p.forEach((ticket) => {
						if (ticket.exists()) {
							smallerList.push(ticket);
						} else {
							this.logger.log('broken timelineTicket', ticket.ref.toString());
						}
					});
					return smallerList;
				});
			}),
		]);
	}
}
